import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter} from '@store/common/tableFilter.model';
import {Account, AccountFilters, AccountPagination, ManageAccount} from './account.model';
import {getAccountDataSource} from './account.requests';
import {accountStore} from './account.store';

export class AccountsService {
  store = accountStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: AccountFilters.initTableFilters(), hideExpired: true},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getAccounts = (): Observable<AccountPagination> => {
    const {filters: {tableFilters, hideExpired}, sort, tablePagination,} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETAccount(),
      params: {
        ...requestFilters,
        hideExpired,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AccountPagination>) => {
          return response.data;
        }),
        tap((accountPagination) => {
          const paginationData = Pagination.toPaginationData(accountPagination, tablePagination.page);

          this.store.update(
            upsertEntities(accountPagination.data),
            upsertEntitiesById(accountPagination.data.map((account) => account.id), {
              updater: (accountUi) => accountUi,
              creator: (accountId) => ({id: accountId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, accountPagination.data.map((account) => account.id)),
            getAccountDataSource.setSuccess(),
          );
        }),
        getAccountDataSource.trackRequestStatus(),
      );
  };


  getAccountById = (accountId: string): Observable<Account> => {
    return from(APIAxios({
      ...APIRoutes.GETAccountById(accountId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Account>) => {
          return response.data;
        }),
      );
  };

  createAccount = (account: ManageAccount) => {
    return from(APIAxios({
      ...APIRoutes.POSTAccount(),
      data: account,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Account>) => {
          return response.data;
        }),
        switchMap(() => this.getAccounts())
      );
  };

  updateAccount = (accountId: string, account: ManageAccount) => {
    console.log(account);
    return from(APIAxios({
      ...APIRoutes.PUTAccount(accountId),
      data: account,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Account>) => {
          return response.data;
        }),
        switchMap(() => this.getAccounts())
      );
  };

  deleteAccountInList = (accountId: number) => {
    return from(APIAxios({
      ...APIRoutes.DELETEAccount(accountId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAccounts()),
      );
  };

  setSelectedAccountIds = (accountIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (accountUI) => ({...accountUI, selected: accountIds.includes(accountUI.id)}),
        {ref: UIEntitiesRef},
      ),
    );
  };
}

export const accountsService = new AccountsService();
